// Single source of truth for which profile fields must be filled before
// share_profile (marketplace / case-referral listing) can be enabled.
// Profile Picture is intentionally excluded.

const isBlankText = (v) => !String(v || '').trim();
const isEmptyList = (v) => !v || v.length === 0;
const isEmptyNumber = (v) => v === '' || v === null || v === undefined;

const hasAvailability = (p) =>
  (p.clinicLocations || []).some(
    (loc) => loc && loc.selectedDays && Object.keys(loc.selectedDays).length > 0
  );

export const PROFILE_SHARE_REQUIRED_FIELDS = [
  { label: 'Name', isMissing: (p) => isBlankText(p.name) },
  { label: 'Gender', isMissing: (p) => !p.gender },
  { label: 'Type/Speciality', isMissing: (p) => !p.practiceType },
  { label: 'About Me', isMissing: (p) => isBlankText(p.about) },
  { label: 'Languages', isMissing: (p) => isEmptyList(p.languages) },
  { label: 'Practice Title', isMissing: (p) => isBlankText(p.practiceTitle) },
  { label: 'Organization', isMissing: (p) => isBlankText(p.organisation) },
  { label: 'Professional Qualification', isMissing: (p) => isBlankText(p.professionalQualification) },
  { label: 'Experience (years)', isMissing: (p) => isEmptyNumber(p.experienceYears) },
  { label: 'Intervention Approach', isMissing: (p) => isEmptyList(p.interventionApproach) },
  { label: 'Assessment Types', isMissing: (p) => isEmptyList(p.assessmentTypes) },
  { label: 'Speciality Areas', isMissing: (p) => isEmptyList(p.preferredCaseTypes) },
  { label: 'Client Group', isMissing: (p) => isEmptyList(p.clientGroups) },
  { label: 'Service Format', isMissing: (p) => isEmptyList(p.serviceFormats) },
  { label: 'First Session Duration', isMissing: (p) => !p.firstSessionDuration },
  { label: 'First Session Price', isMissing: (p) => isEmptyNumber(p.firstSessionPrice) },
  { label: 'Regular Session Duration', isMissing: (p) => !p.regularSessionDuration },
  { label: 'Regular Session Price', isMissing: (p) => isEmptyNumber(p.regularSessionPrice) },
  { label: 'Clinic Locations', isMissing: (p) => isEmptyList(p.clinicLocations) },
  { label: 'Availability', isMissing: (p) => !hasAvailability(p) },
  { label: 'Phone Number', isMissing: (p) => isBlankText(p.phoneNumber) },
  { label: 'Address', isMissing: (p) => isBlankText(p.address) },
  { label: 'Contact Email', isMissing: (p) => isBlankText(p.secondaryEmail) },
];

// Returns the labels of required fields that are still empty.
export const getMissingProfileFields = (profile) =>
  PROFILE_SHARE_REQUIRED_FIELDS.filter((f) => f.isMissing(profile)).map((f) => f.label);

// -----------------------------------------------------------------------------
// Public-profile section cards (onboarding step 5 grid + step 6 checklist).
//
// Sections group the flat required-field list into the cards a user actually
// edits. `badge` is presentation only ('required' | 'recommended'); the
// share_profile gate always runs on the FULL required list above, so a
// 'recommended' card can still contain share-required fields (e.g. Therapy
// approaches — badge per design, but interventionApproach is share-required).
// `isComplete` decides the card's Added / Not added state.
// -----------------------------------------------------------------------------

export const PROFILE_SECTIONS = [
  {
    key: 'photo',
    label: 'Profile photo',
    badge: 'recommended',
    description: 'Add a professional photo to help clients recognize you.',
    isComplete: (p) => !isBlankText(p.profilePictureUrl),
  },
  {
    key: 'about',
    label: 'About me',
    badge: 'required',
    description: 'Introduce yourself and your approach.',
    // Carries the share-required identity/contact fields the design's card
    // grid doesn't surface anywhere else.
    isComplete: (p) =>
      !isBlankText(p.about) &&
      Boolean(p.gender) &&
      !isBlankText(p.phoneNumber) &&
      !isBlankText(p.address) &&
      !isBlankText(p.secondaryEmail),
  },
  {
    key: 'languages',
    label: 'Languages',
    badge: 'required',
    description: 'Select the languages you provide therapy in.',
    isComplete: (p) => !isEmptyList(p.languages),
  },
  {
    key: 'specialties',
    label: 'Specialties',
    badge: 'required',
    description: 'Choose the areas you specialize in.',
    isComplete: (p) =>
      !isEmptyList(p.preferredCaseTypes) &&
      !isEmptyList(p.assessmentTypes) &&
      !isEmptyList(p.clientGroups),
  },
  {
    key: 'approaches',
    label: 'Therapy approaches',
    badge: 'recommended',
    description: 'Add the therapy approaches you use in your practice.',
    isComplete: (p) => !isEmptyList(p.interventionApproach),
  },
  {
    key: 'availability',
    label: 'Availability',
    badge: 'required',
    description: 'Set your available days and times.',
    isComplete: (p) => hasAvailability(p),
  },
  {
    key: 'fees',
    label: 'Session fees',
    badge: 'recommended',
    description: 'Set your session fees. This will be shown publicly.',
    isComplete: (p) =>
      Boolean(p.firstSessionDuration) &&
      !isEmptyNumber(p.firstSessionPrice) &&
      Boolean(p.regularSessionDuration) &&
      !isEmptyNumber(p.regularSessionPrice) &&
      !isEmptyList(p.serviceFormats),
  },
];

export const getProfileSectionStatus = (profile) =>
  PROFILE_SECTIONS.map((s) => ({ ...s, complete: s.isComplete(profile) }));

// Step-6 "enable case referrals" checklist rows. Registry verification comes
// from outside the profile object. Stripe payout setup is intentionally not a
// referral-sharing prerequisite.
export const getReferralChecklist = (profile, { verified }) => {
  const sections = getProfileSectionStatus(profile);
  const sectionComplete = (key) => sections.find((s) => s.key === key)?.complete || false;
  const publicProfileDone = ['about', 'languages', 'specialties'].every(sectionComplete);
  const allFieldsDone = getMissingProfileFields(profile).length === 0;
  return [
    {
      key: 'professional_info',
      label: 'Professional information',
      complete: !isBlankText(profile.practiceTitle) && !isBlankText(profile.organisation),
    },
    {
      key: 'qualifications',
      label: 'Qualifications',
      complete:
        !isBlankText(profile.professionalQualification) &&
        !isEmptyNumber(profile.experienceYears),
    },
    { key: 'certification', label: 'Certification verified', complete: Boolean(verified) },
    { key: 'public_profile', label: 'Public profile', complete: publicProfileDone },
    { key: 'availability', label: 'Availability', complete: hasAvailability(profile) },
    { key: 'completion', label: 'Profile completion', complete: allFieldsDone },
  ];
};
